import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import PageWrapper from '@/components/layout/PageWrapper';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { usersApi, vendorsApi, equipmentTypesApi, locationsApi, companiesListApi, auditLogApi, settingsApi, notificationsApi } from '@/api';
import type { NotifSubscription } from '@/api';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { User, UserRole, AuditLogEntry } from '@/types';

// ─── Audit diff ───────────────────────────────────────────────────────────────

const SKIP_KEYS = new Set(['updatedAt', 'createdAt', 'updated_at', 'created_at', 'photoKey', 'photo_key']);

function AuditDiff({ entry }: { entry: AuditLogEntry }) {
  if (entry.action === 'INSERT') {
    return <span className="text-xs text-muted-foreground">New record</span>;
  }
  if (entry.action === 'DELETE') {
    return <span className="text-xs text-muted-foreground">Record deleted</span>;
  }
  if (!entry.oldValues || !entry.newValues) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const changed = Object.keys(entry.newValues).filter(
    (k) => !SKIP_KEYS.has(k) && JSON.stringify(entry.oldValues![k]) !== JSON.stringify(entry.newValues![k])
  );

  if (changed.length === 0) {
    return <span className="text-xs text-muted-foreground">No field changes</span>;
  }

  return (
    <div className="space-y-1">
      {changed.map((k) => (
        <div key={k} className="text-xs">
          <span className="font-medium">{k}:</span>{' '}
          <span className="text-red-500 line-through">{String(entry.oldValues![k] ?? '—')}</span>
          {' → '}
          <span className="text-green-600">{String(entry.newValues![k] ?? '—')}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Shared lookup table ──────────────────────────────────────────────────────

interface LookupRow { id: number; name: string; isActive: boolean; }

function LookupTable({ title, rows, newName, onNewNameChange, onAdd, onToggle, addLabel, placeholder, isPending }: {
  title: string; rows: LookupRow[]; newName: string;
  onNewNameChange: (v: string) => void; onAdd: () => void;
  onToggle: (id: number, isActive: boolean) => void;
  addLabel: string; placeholder: string; isPending: boolean;
}) {
  return (
    <div>
      <h2 className="text-base font-semibold mb-3">{title}</h2>
      <div className="rounded-md border border-border bg-card overflow-hidden max-w-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>
                  <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    row.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                 : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400')}>
                    {row.isActive ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => onToggle(row.id, !row.isActive)}>
                    {row.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <Input className="h-8 w-44 text-sm" placeholder={placeholder}
                  value={newName} onChange={(e) => onNewNameChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) onAdd(); }} />
              </TableCell>
              <TableCell colSpan={2}>
                <Button size="sm" disabled={!newName.trim() || isPending} onClick={onAdd}>{addLabel}</Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Nav items ────────────────────────────────────────────────────────────────

type Section = 'general' | 'users' | 'equipment-types' | 'locations' | 'companies' | 'audit-log' | 'notifications';

const NAV: { id: Section; label: string; group?: string }[] = [
  { id: 'general',         label: 'General' },
  { id: 'users',           label: 'Users' },
  { id: 'equipment-types', label: 'Equipment Types', group: 'Lookups' },
  { id: 'locations',       label: 'Locations',       group: 'Lookups' },
  { id: 'companies',       label: 'Companies',       group: 'Lookups' },
  { id: 'notifications',   label: 'Notifications',   group: 'System' },
  { id: 'audit-log',       label: 'Audit Log',       group: 'System' },
];

// ─── Role config ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS: { label: string; value: UserRole }[] = [
  { label: 'Super Admin',     value: 'super_admin' },
  { label: 'PM Admin',        value: 'pm_admin' },
  { label: 'Equipment Admin', value: 'equipment_admin' },
  { label: 'Repair Tech',     value: 'repair_tech' },
  { label: 'Submitter',       value: 'submitter' },
  { label: 'Accounting',      value: 'accounting' },
  { label: 'Ext. Vendor',     value: 'ext_vendor' },
];

const ROLE_COLORS: Record<UserRole, string> = {
  super_admin:      'text-red-600 dark:text-red-400',
  pm_admin:         'text-purple-600 dark:text-purple-400',
  equipment_admin:  'text-indigo-600 dark:text-indigo-400',
  repair_tech:      'text-blue-600 dark:text-blue-400',
  submitter:        'text-gray-600 dark:text-gray-400',
  accounting:       'text-green-600 dark:text-green-400',
  ext_vendor:       'text-orange-600 dark:text-orange-400',
};

// ─── Notifications section ────────────────────────────────────────────────────

const SUBSCRIBER_EVENTS: { key: string; label: string; description: string }[] = [
  { key: 'new_repair',            label: 'New Repair Order',           description: 'When a manual repair order is submitted' },
  { key: 'pm_repair_created',     label: 'PM Repair Order Created',    description: 'When a PM work order is auto-created' },
  { key: 'pm_overdue',            label: 'PM Due / Overdue Alert',     description: 'When a PM assignment is due or overdue' },
  { key: 'parts_credit_card',     label: 'Parts — Credit Card',        description: 'When a parts order is completed via credit card' },
  { key: 'parts_business_account',label: 'Parts — Business Account',   description: 'When a parts order is completed via business account' },
  { key: 'parts_other',           label: 'Parts — Other Payment',      description: 'When a parts order is completed via other payment' },
];

const TOGGLE_EVENTS: { key: string; label: string; description: string; defaultValue: boolean }[] = [
  { key: 'mechanic_assigned_notify_mechanic', label: 'Notify mechanic when assigned',      description: 'Email the assigned mechanic when a repair is assigned to them',       defaultValue: true },
  { key: 'repair_closed_notify_submitter',    label: 'Notify submitter when closed',       description: 'Email the submitter when their repair request is marked Done',         defaultValue: true },
  { key: 'repair_status_changed',             label: 'Notify submitter on status changes', description: 'Email the submitter whenever the repair status changes (any status)',  defaultValue: false },
];

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin', pm_admin: 'PM Admin', equipment_admin: 'Equipment Admin',
  repair_tech: 'Repair Tech', submitter: 'Submitter', accounting: 'Accounting', ext_vendor: 'Ext Vendor',
};
const ALL_ROLES = ['super_admin', 'pm_admin', 'equipment_admin', 'repair_tech', 'submitter', 'accounting', 'ext_vendor'];

function NotificationsSection({ users }: { users: { id: number; name: string; email: string; active: boolean }[] }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['notifications'], queryFn: notificationsApi.get });

  const [addState, setAddState] = useState<Record<string, { targetType: 'user' | 'role'; targetId: string; channel: 'email' | 'sms' | 'both' }>>({});

  const removeMutation = useMutation({
    mutationFn: (id: number) => notificationsApi.removeSubscription(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const addMutation = useMutation({
    mutationFn: (payload: Parameters<typeof notificationsApi.addSubscription>[0]) =>
      notificationsApi.addSubscription(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean }) =>
      notificationsApi.patchToggle(key, value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const subsByEvent = useMemo(() => {
    const map: Record<string, NotifSubscription[]> = {};
    for (const s of data?.subscriptions ?? []) {
      (map[s.eventKey] ??= []).push(s);
    }
    return map;
  }, [data]);

  const activeUsers = users.filter((u) => u.active);

  function getAdd(key: string) {
    return addState[key] ?? { targetType: 'user' as const, targetId: '', channel: 'email' as const };
  }
  function setAdd(key: string, patch: Partial<typeof addState[string]>) {
    setAddState((prev) => ({ ...prev, [key]: { ...getAdd(key), ...patch } }));
  }

  function handleAdd(eventKey: string) {
    const s = getAdd(eventKey);
    if (!s.targetId) return;
    const payload = s.targetType === 'user'
      ? { eventKey, targetType: 'user' as const, userId: Number(s.targetId), channel: s.channel }
      : { eventKey, targetType: 'role' as const, targetRole: s.targetId, channel: s.channel };
    addMutation.mutate(payload, { onSuccess: () => setAdd(eventKey, { targetId: '' }) });
  }

  if (isLoading) return <div className="text-muted-foreground text-sm py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-8">
      {/* Alert Subscriptions */}
      <div>
        <h2 className="text-base font-semibold mb-1">Alert Subscriptions</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Configure who gets notified for each event. Add individual users or entire roles.
        </p>
        <div className="space-y-6">
          {SUBSCRIBER_EVENTS.map((ev) => {
            const subs = subsByEvent[ev.key] ?? [];
            const s = getAdd(ev.key);
            return (
              <div key={ev.key} className="rounded-md border border-border bg-card p-4 space-y-3">
                <div>
                  <p className="font-medium text-sm">{ev.label}</p>
                  <p className="text-xs text-muted-foreground">{ev.description}</p>
                </div>

                {subs.length > 0 && (
                  <div className="rounded border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="py-1.5 text-xs">Target</TableHead>
                          <TableHead className="py-1.5 text-xs">Type</TableHead>
                          <TableHead className="py-1.5 text-xs">Channel</TableHead>
                          <TableHead className="py-1.5 text-xs"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subs.map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell className="py-1.5 text-sm">
                              {sub.targetType === 'user'
                                ? (sub.user?.fullName ?? sub.user?.email ?? `User #${sub.userId}`)
                                : (ROLE_LABELS[sub.roleName ?? ''] ?? sub.roleName)}
                            </TableCell>
                            <TableCell className="py-1.5">
                              <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                sub.targetType === 'user'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400')}>
                                {sub.targetType === 'user' ? 'User' : 'Role'}
                              </span>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground capitalize">
                                {sub.channel}
                              </span>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                                onClick={() => removeMutation.mutate(sub.id)}>
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Add row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={s.targetType} onValueChange={(v) => setAdd(ev.key, { targetType: v as 'user' | 'role', targetId: '' })}>
                    <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="role">Role</SelectItem>
                    </SelectContent>
                  </Select>

                  {s.targetType === 'user' ? (
                    <Select value={s.targetId} onValueChange={(v) => setAdd(ev.key, { targetId: v })}>
                      <SelectTrigger className="h-8 w-48 text-xs"><SelectValue placeholder="Select user…" /></SelectTrigger>
                      <SelectContent>
                        {activeUsers.map((u) => (
                          <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={s.targetId} onValueChange={(v) => setAdd(ev.key, { targetId: v })}>
                      <SelectTrigger className="h-8 w-48 text-xs"><SelectValue placeholder="Select role…" /></SelectTrigger>
                      <SelectContent>
                        {ALL_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Select value={s.channel} onValueChange={(v) => setAdd(ev.key, { channel: v as 'email' | 'sms' | 'both' })}>
                    <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button size="sm" className="h-8 text-xs" disabled={!s.targetId || addMutation.isPending}
                    onClick={() => handleAdd(ev.key)}>
                    Add
                  </Button>
                </div>

                {subs.length === 0 && (
                  <p className="text-xs text-muted-foreground italic">No subscribers — nobody will be notified for this event.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Behavior Toggles */}
      <div>
        <h2 className="text-base font-semibold mb-1">Behavior Toggles</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Control automatic notification behaviors.
        </p>
        <div className="rounded-md border border-border bg-card divide-y divide-border">
          {TOGGLE_EVENTS.map((ev) => {
            const value = data?.toggles?.[ev.key] ?? ev.defaultValue;
            return (
              <div key={ev.key} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{ev.label}</p>
                  <p className="text-xs text-muted-foreground">{ev.description}</p>
                </div>
                <Switch
                  checked={value}
                  disabled={toggleMutation.isPending}
                  onCheckedChange={(checked: boolean) => toggleMutation.mutate({ key: ev.key, value: checked })}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Admin() {
  const queryClient = useQueryClient();
  const [section, setSection] = useState<Section>('general');

  // ── General settings ───────────────────────────────────────────────────────
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });
  const [pendingTimezone, setPendingTimezone] = useState<string>('');

  const saveSettingsMutation = useMutation({
    mutationFn: (data: { timezone: string; mechanicNotesRequired: boolean }) => settingsApi.patch(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['settings'], updated);
      toast({ title: 'Settings saved' });
    },
    onError: () => toast({ title: 'Failed to save settings', variant: 'destructive' }),
  });

  const currentTimezone = pendingTimezone || settings?.timezone || 'America/Chicago';
  const mechanicNotesRequired = settings?.mechanicNotesRequired === 'true';

  // ── Users ──────────────────────────────────────────────────────────────────
  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const { data: vendors } = useQuery({ queryKey: ['vendors'], queryFn: () => vendorsApi.list() });
  const [phoneEdits, setPhoneEdits] = useState<Record<number, string>>({});

  // Invite user state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('submitter');

  const inviteMutation = useMutation({
    mutationFn: () => usersApi.invite({ email: inviteEmail.trim(), fullName: inviteFullName.trim(), role: inviteRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'Invitation sent', description: `${inviteEmail} will receive a login email.` });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteRole('submitter');
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to invite user';
      toast({ title: 'Invite failed', description: message, variant: 'destructive' });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: number; role: UserRole }) => usersApi.updateRole(id, role),
    onSuccess: (updated) => {
      queryClient.setQueryData<User[]>(['users'], (prev) => prev?.map((u) => u.id === updated.id ? updated : u) ?? []);
      toast({ title: 'Role updated' });
    },
    onError: () => toast({ title: 'Failed to update role', variant: 'destructive' }),
  });

  const assignVendorMutation = useMutation({
    mutationFn: ({ id, vendorId }: { id: number; vendorId: number | null }) => usersApi.assignVendor(id, vendorId),
    onSuccess: (updated) => {
      queryClient.setQueryData<User[]>(['users'], (prev) => prev?.map((u) => u.id === updated.id ? updated : u) ?? []);
      toast({ title: 'Vendor assigned' });
    },
    onError: () => toast({ title: 'Failed to assign vendor', variant: 'destructive' }),
  });

  const updatePhoneMutation = useMutation({
    mutationFn: ({ id, phoneNumber }: { id: number; phoneNumber: string | null }) => usersApi.updatePhone(id, phoneNumber),
    onSuccess: (updated) => {
      queryClient.setQueryData<User[]>(['users'], (prev) => prev?.map((u) => u.id === updated.id ? updated : u) ?? []);
      toast({ title: 'Phone updated' });
    },
    onError: () => toast({ title: 'Failed to update phone', variant: 'destructive' }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => usersApi.setActive(id, isActive),
    onSuccess: (updated) => {
      queryClient.setQueryData<User[]>(['users'], (prev) => prev?.map((u) => u.id === updated.id ? updated : u) ?? []);
      toast({ title: updated.active ? 'User enabled' : 'User disabled' });
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed to update user';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (id: number) => usersApi.resendInvite(id),
    onSuccess: () => toast({ title: 'Invitation resent' }),
    onError: () => toast({ title: 'Failed to resend invitation', variant: 'destructive' }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: number) => usersApi.resetPassword(id),
    onSuccess: () => toast({ title: 'Password reset email sent' }),
    onError: () => toast({ title: 'Failed to send password reset', variant: 'destructive' }),
  });

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersApi.delete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData<User[]>(['users'], (prev) => prev?.filter((u) => u.id !== id) ?? []);
      toast({ title: 'User deleted' });
      setDeleteConfirmId(null);
    },
    onError: () => toast({ title: 'Failed to delete user', variant: 'destructive' }),
  });

  // ── Lookups ────────────────────────────────────────────────────────────────
  const { data: equipmentTypes } = useQuery({ queryKey: ['equipment-types'], queryFn: () => equipmentTypesApi.list() });
  const { data: locationsList }  = useQuery({ queryKey: ['locations'],        queryFn: () => locationsApi.list() });
  const { data: companiesList }  = useQuery({ queryKey: ['companies-list'],   queryFn: () => companiesListApi.list() });

  const [newTypeName,     setNewTypeName]     = useState('');
  const [newLocationName, setNewLocationName] = useState('');
  const [newCompanyName,  setNewCompanyName]  = useState('');

  const addTypeMutation = useMutation({
    mutationFn: (name: string) => equipmentTypesApi.create({ name }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['equipment-types'] }); setNewTypeName(''); toast({ title: 'Equipment type added' }); },
    onError: () => toast({ title: 'Failed to add type', variant: 'destructive' }),
  });
  const toggleTypeMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => equipmentTypesApi.update(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['equipment-types'] }),
    onError: () => toast({ title: 'Failed to update', variant: 'destructive' }),
  });

  const addLocationMutation = useMutation({
    mutationFn: (name: string) => locationsApi.create({ name }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['locations'] }); setNewLocationName(''); toast({ title: 'Location added' }); },
    onError: () => toast({ title: 'Failed to add location', variant: 'destructive' }),
  });
  const toggleLocationMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => locationsApi.update(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
    onError: () => toast({ title: 'Failed to update', variant: 'destructive' }),
  });

  const addCompanyMutation = useMutation({
    mutationFn: (name: string) => companiesListApi.create({ name }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['companies-list'] }); setNewCompanyName(''); toast({ title: 'Company added' }); },
    onError: () => toast({ title: 'Failed to add company', variant: 'destructive' }),
  });
  const toggleCompanyMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => companiesListApi.update(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['companies-list'] }),
    onError: () => toast({ title: 'Failed to update', variant: 'destructive' }),
  });

  // ── Audit Log ─────────────────────────────────────────────────────────────
  const [auditTableFilter, setAuditTableFilter] = useState('all');
  const [auditActionFilter, setAuditActionFilter] = useState('all');
  const [auditOffset, setAuditOffset] = useState(0);
  const AUDIT_LIMIT = 50;

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['audit-log', auditTableFilter, auditActionFilter, auditOffset],
    queryFn: () => auditLogApi.list({
      ...(auditTableFilter !== 'all' ? { table: auditTableFilter } : {}),
      ...(auditActionFilter !== 'all' ? { action: auditActionFilter } : {}),
      limit: AUDIT_LIMIT,
      offset: auditOffset,
    }),
    enabled: section === 'audit-log',
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  const groups = [...new Set(NAV.map((n) => n.group ?? ''))];

  return (
    <PageWrapper title="Administration" subtitle="Manage users, roles, and system configuration">
      <div className="flex gap-8">

        {/* Side nav */}
        <nav className="w-48 shrink-0">
          {groups.map((group) => (
            <div key={group} className="mb-4">
              {group && (
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </p>
              )}
              {NAV.filter((n) => (n.group ?? '') === group).map((n) => (
                <button
                  key={n.id}
                  onClick={() => setSection(n.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                    section === n.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  {n.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">

          {section === 'general' && (
            <div className="max-w-md space-y-6">
              <div>
                <h2 className="text-base font-semibold mb-1">General Settings</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Settings that apply to your entire organization.
                </p>
              </div>

              <div className="rounded-md border border-border bg-card p-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Time Zone</label>
                  <p className="text-xs text-muted-foreground">
                    Used for date calculations — repair request dates, PM schedules, and reports.
                  </p>
                  <Select
                    value={currentTimezone}
                    onValueChange={(v) => setPendingTimezone(v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {[
                        'America/New_York',
                        'America/Chicago',
                        'America/Denver',
                        'America/Phoenix',
                        'America/Los_Angeles',
                        'America/Anchorage',
                        'Pacific/Honolulu',
                        'America/Puerto_Rico',
                      ].map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')} — {tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Mechanic Notes Required</label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, a repair request cannot be marked Done unless mechanic notes have been entered.
                  </p>
                  <Select
                    value={mechanicNotesRequired ? 'true' : 'false'}
                    onValueChange={(v) =>
                      saveSettingsMutation.mutate({ timezone: currentTimezone, mechanicNotesRequired: v === 'true' })
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">Not required</SelectItem>
                      <SelectItem value="true">Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  size="sm"
                  disabled={saveSettingsMutation.isPending || !pendingTimezone || pendingTimezone === settings?.timezone}
                  onClick={() => saveSettingsMutation.mutate({ timezone: currentTimezone, mechanicNotesRequired })}
                >
                  {saveSettingsMutation.isPending ? 'Saving…' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}

          {section === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">Users</h2>
                <Button size="sm" onClick={() => setInviteOpen(true)}>
                  + Invite User
                </Button>
              </div>
            <div className="rounded-md border border-border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}><div className="h-4 animate-pulse rounded bg-muted" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (users?.length ?? 0) === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">No users found.</TableCell>
                    </TableRow>
                  ) : (
                    users?.map((user) => (
                      <TableRow key={user.id} className={cn(!user.active && 'opacity-60')}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{user.email}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{user.company ?? '—'}</TableCell>
                        <TableCell>
                          <Select value={user.role} onValueChange={(role) => updateRoleMutation.mutate({ id: user.id, role: role as UserRole })}>
                            <SelectTrigger className={cn('w-44 h-8 text-sm', ROLE_COLORS[user.role])}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value} className={ROLE_COLORS[opt.value]}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {user.role === 'ext_vendor' ? (
                            <Select
                              value={user.vendorId != null ? String(user.vendorId) : 'none'}
                              onValueChange={(val) => assignVendorMutation.mutate({ id: user.id, vendorId: val === 'none' ? null : Number(val) })}
                            >
                              <SelectTrigger className="w-44 h-8 text-sm"><SelectValue placeholder="Assign vendor…" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— None —</SelectItem>
                                {vendors?.map((v) => <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            className="h-8 w-36 text-sm"
                            placeholder="+1 555 000 0000"
                            value={phoneEdits[user.id] ?? user.phoneNumber ?? ''}
                            onChange={(e) => setPhoneEdits((prev) => ({ ...prev, [user.id]: e.target.value }))}
                            onBlur={() => {
                              const raw = (phoneEdits[user.id] ?? '').trim();
                              let val: string | null = null;
                              if (raw) {
                                const digits = raw.replace(/\D/g, '');
                                if (digits.length === 10) val = `+1${digits}`;
                                else if (digits.length === 11 && digits.startsWith('1')) val = `+${digits}`;
                                else val = raw.startsWith('+') ? raw : `+${digits}`;
                                setPhoneEdits((prev) => ({ ...prev, [user.id]: val! }));
                              }
                              if (val !== (user.phoneNumber ?? null)) updatePhoneMutation.mutate({ id: user.id, phoneNumber: val });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            user.active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400')}>
                            {user.active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={toggleActiveMutation.isPending}
                              onClick={() => toggleActiveMutation.mutate({ id: user.id, isActive: !user.active })}
                            >
                              {user.active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={resendInviteMutation.isPending}
                              onClick={() => resendInviteMutation.mutate(user.id)}
                            >
                              Resend Invite
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={resetPasswordMutation.isPending}
                              onClick={() => resetPasswordMutation.mutate(user.id)}
                            >
                              Reset Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              onClick={() => setDeleteConfirmId(user.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            </div>
          )}

          {/* Invite User Dialog */}
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Invite New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Full Name</label>
                  <Input
                    placeholder="Jane Smith"
                    value={inviteFullName}
                    onChange={(e) => setInviteFullName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    placeholder="jane@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Role</label>
                  <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as UserRole)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button
                  disabled={!inviteEmail.trim() || !inviteFullName.trim() || inviteMutation.isPending}
                  onClick={() => inviteMutation.mutate()}
                >
                  {inviteMutation.isPending ? 'Sending…' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete User Confirmation Dialog */}
          <Dialog open={deleteConfirmId !== null} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Delete User</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                This will permanently remove the user from the system and revoke their login access. This cannot be undone.
              </p>
              {deleteConfirmId !== null && (() => {
                const u = users?.find((x) => x.id === deleteConfirmId);
                return u ? (
                  <p className="text-sm font-medium">{u.name} — {u.email}</p>
                ) : null;
              })()}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteConfirmId !== null && deleteMutation.mutate(deleteConfirmId)}
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete User'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {section === 'equipment-types' && (
            <LookupTable
              title="Equipment Types"
              rows={equipmentTypes ?? []}
              newName={newTypeName} onNewNameChange={setNewTypeName}
              onAdd={() => newTypeName.trim() && addTypeMutation.mutate(newTypeName.trim())}
              onToggle={(id, isActive) => toggleTypeMutation.mutate({ id, isActive })}
              addLabel="Add Type" placeholder="e.g. Crane" isPending={addTypeMutation.isPending}
            />
          )}

          {section === 'locations' && (
            <LookupTable
              title="Locations"
              rows={locationsList ?? []}
              newName={newLocationName} onNewNameChange={setNewLocationName}
              onAdd={() => newLocationName.trim() && addLocationMutation.mutate(newLocationName.trim())}
              onToggle={(id, isActive) => toggleLocationMutation.mutate({ id, isActive })}
              addLabel="Add Location" placeholder="e.g. Yard A" isPending={addLocationMutation.isPending}
            />
          )}

          {section === 'companies' && (
            <LookupTable
              title="Companies"
              rows={companiesList ?? []}
              newName={newCompanyName} onNewNameChange={setNewCompanyName}
              onAdd={() => newCompanyName.trim() && addCompanyMutation.mutate(newCompanyName.trim())}
              onToggle={(id, isActive) => toggleCompanyMutation.mutate({ id, isActive })}
              addLabel="Add Company" placeholder="e.g. Acme LLC" isPending={addCompanyMutation.isPending}
            />
          )}

          {section === 'notifications' && (
            <NotificationsSection users={users ?? []} />
          )}

          {section === 'audit-log' && (
            <div>
              <h2 className="text-base font-semibold mb-3">Audit Log</h2>

              {/* Filters */}
              <div className="flex items-center gap-3 mb-4">
                <Select value={auditTableFilter} onValueChange={(v) => { setAuditTableFilter(v); setAuditOffset(0); }}>
                  <SelectTrigger className="w-44 h-8 text-sm">
                    <SelectValue placeholder="All tables" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tables</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="repair_requests">Repair Requests</SelectItem>
                    <SelectItem value="ordered_parts">Ordered Parts</SelectItem>
                    <SelectItem value="pm_schedules">PM Schedules</SelectItem>
                    <SelectItem value="vendors">Vendors</SelectItem>
                    <SelectItem value="users">Users</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={auditActionFilter} onValueChange={(v) => { setAuditActionFilter(v); setAuditOffset(0); }}>
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="INSERT">Created</SelectItem>
                    <SelectItem value="UPDATE">Updated</SelectItem>
                    <SelectItem value="DELETE">Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Who</TableHead>
                      <TableHead>Table</TableHead>
                      <TableHead>Record</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLoading ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 6 }).map((_, j) => (
                            <TableCell key={j}><div className="h-4 animate-pulse rounded bg-muted" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (auditData?.items.length ?? 0) === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No audit entries found.</TableCell>
                      </TableRow>
                    ) : (
                      auditData?.items.map((entry: AuditLogEntry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {new Date(entry.changedAt).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm">{entry.changedByName ?? '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.tableName}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">#{entry.recordId}</TableCell>
                          <TableCell>
                            <span className={cn(
                              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                              entry.action === 'INSERT' && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                              entry.action === 'UPDATE' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                              entry.action === 'DELETE' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                            )}>
                              {entry.action === 'INSERT' ? 'Created' : entry.action === 'UPDATE' ? 'Updated' : 'Deleted'}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <AuditDiff entry={entry} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {(auditData?.items.length ?? 0) > 0 && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-sm text-muted-foreground">
                    Showing {auditOffset + 1}–{auditOffset + (auditData?.items.length ?? 0)}
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={auditOffset === 0}
                      onClick={() => setAuditOffset(Math.max(0, auditOffset - AUDIT_LIMIT))}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline"
                      disabled={(auditData?.items.length ?? 0) < AUDIT_LIMIT}
                      onClick={() => setAuditOffset(auditOffset + AUDIT_LIMIT)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </PageWrapper>
  );
}
